import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import PhoneMockup from "@/components/PhoneMockup";
import { Check, Minus, Sparkles, Loader2, Star, Zap, FileText, ShieldCheck, Clock, Users, Smartphone, Fingerprint, Image as ImageIcon } from "lucide-react";

function fmtAud(n) {
 if (n == null) return ",";
 return "$" + (Math.round(n * 100) / 100).toFixed(2);
}

/**
 * Pricing page. five-tier money-mode rebuild.
 *
 * Free. top-of-funnel try-it tile (no signup CTA blocker)
 * Vault. entry paid: storage + download rights
 * Shield. the recommended plan (Sonnet AI + letters + bundles)
 * Dispute. burst plan for tribunal week
 * Pro. power users / advocates
 *
 * Each tile has a use-case framing line ("Right for you if…"), 5 concrete
 * features, and a clear CTA. A full comparison matrix lives at the bottom
 * for visitors who want to compare side-by-side before clicking.
 */

const FEATURE_MATRIX = [
  { section: "Case and storage" },
  { row: "Cases", free: "1", vault: "No fixed limit", copilot: "No fixed limit", dispute: "No fixed limit", pro: "No fixed limit" },
  { row: "Storage", free: "250 MB", vault: "5 GB", copilot: "5 GB", dispute: "5 GB", pro: "50 GB" },

  { section: "Documents and evidence" },
  { row: "Lease + bond receipt extraction", free: true, vault: true, copilot: true, dispute: true, pro: true },
  { row: "EXIF forensic check on photos", free: true, vault: true, copilot: true, dispute: true, pro: true },
  { row: "Photo Trail (monthly check-ins, safely dated)", free: false, vault: true, copilot: true, dispute: true, pro: true },
  { row: "Chronology / timeline ruler", free: true, vault: true, copilot: true, dispute: true, pro: true },
  { row: "View documents in-app", free: true, vault: true, copilot: true, dispute: true, pro: true },
  { row: "Download documents", free: false, vault: true, copilot: true, dispute: true, pro: true },
  { row: "Evidence bundle PDF (tribunal-ready)", free: false, vault: true, copilot: true, dispute: true, pro: true },
  { row: "Inspection report extraction", free: false, vault: false, copilot: true, dispute: true, pro: true },

  { section: "AI assistance" },
  { row: "AI Q&A allowance / month", free: "5", vault: "10", copilot: "100", dispute: "500 / 30 days", pro: "750" },
  { row: "Daily AI fair-use cap", free: "3", vault: "5", copilot: "25", dispute: "50", pro: "50" },
  { row: "AI quality", free: "Quick AI", vault: "Quick AI", copilot: "Deep AI", dispute: "Deep AI", pro: "Deep AI" },
  { row: "AI cites state Act sections", free: true, vault: true, copilot: true, dispute: true, pro: true },
  { row: "Letter drafting (template + AI)", free: false, vault: false, copilot: true, dispute: true, pro: true },
  { row: "Email letters direct to agent (with audit trail)", free: false, vault: false, copilot: true, dispute: true, pro: true },

  { section: "Voice notes" },
  { row: "Voice notes (audio kept + manual transcribe)", free: "1 / day", vault: "5 / day", copilot: "20 / day", dispute: "60 / day", pro: "60 / day" },
  { row: "Tamper-evident share link for voice notes", free: true, vault: true, copilot: true, dispute: true, pro: true },
  { row: "Voice + transcript on the timeline", free: true, vault: true, copilot: true, dispute: true, pro: true },
  { row: "Voice \u2192 tribunal-ready statement", free: false, vault: false, copilot: true, dispute: true, pro: true },

  { section: "Extras and support" },
  { row: "Property history check", free: false, vault: false, copilot: "3 / month", dispute: "Included", pro: "Included" },
  { row: "Public Q&A snapshots", free: false, vault: false, copilot: true, dispute: true, pro: true },
  { row: "Support response time", free: "Help guide", vault: "Email · 3 days", copilot: "Email · 1 day", dispute: "Email · same day", pro: "Priority · same day" },
];

const TILES = {
 free: {
 name: "Free",
 blurb: "Get started without a credit card.",
 icon: FileText,
 persona: "I just want to keep my lease and bond receipt safe.",
 bullets: [
 "1 case",
 "250 MB storage (lease + receipt + a few photos)",
 "5 AI questions per month (Quick AI)",
 "Read-only, no downloads or bundle exports",
 "Voice notes: 1 transcription/day, 3 minutes max",
 "Help Center + Tenant Brain access",
 ],
 cta: "Start free",
 ctaPath: "/register",
 },
 vault: {
 name: "Vault",
 blurb: "Your forever-record of every notice, email and photo.",
 icon: ShieldCheck,
 persona: "I want a permanent paper trail in case things go sour later.",
 bullets: [
 "Multiple cases, kept side by side",
 "5 GB storage with EXIF preserved",
 "Download every document any time",
 "Evidence bundle PDF (tribunal-ready)",
 "Voice notes: 5 transcriptions/day, 5 minutes per note",
 "10 Quick AI questions per month",
 "2 AI-drafted letters per month, a taste of the AI",
 ],
 },
 copilot: {
 name: "Shield",
 blurb: "Everything Compass can do for you, unlocked all month.",
 icon: Sparkles,
 persona: "I want help with the paperwork before it becomes a problem.",
 bullets: [
 "Everything in Vault",
 "100 friendly chats with Compass each month",
 "Compass writes calm, smart replies to your agent",
 "Send letters straight to the agent, we keep a copy",
 "Voice notes: 20 transcriptions/day, 10 minutes per note",
 "Voice-note what happened, we tidy it into a clear statement",
 ],
 badge: "Most popular",
 },
 dispute: {
 name: "Dispute & Tribunal Mode",
 shortName: "Dispute & Tribunal",
 blurb: "30 days of high-allowance AI for the week you actually need it.",
 icon: Zap,
 persona: "I'm heading to QCAT/NCAT/VCAT next month. I need everything, now.",
 bullets: [
 "Everything in Shield, unlocked",
 "500 Deep AI questions over 30 days",
 "Voice notes: 60 transcriptions/day, 20 minutes per note",
 "Generous property history checks",
 "Same-day email support",
 "Priority processing on uploads",
 ],
 badge: "Pay once",
 },
 pro: {
 name: "Pro",
 blurb: "For tenant advocates and households juggling more than one tenancy.",
 icon: Users,
 persona: "I help other renters, or I'm juggling more than one tenancy.",
 bullets: [
 "Run multiple cases and tenancies side by side, all in one place",
 "The highest Deep AI allowance, so you never hit a limit mid-dispute",
 "Unlimited property history checks. Vet any place or agent before you sign",
 "50 GB storage for years of records, photos and voice notes",
 "Priority support, same business day",
 ],
 },
};

const TILE_ORDER = ["free", "vault", "copilot", "pro"];

export default function Pricing() {
 const { user } = useAuth();
 const navigate = useNavigate();
 const [plans, setPlans] = useState([]);
 const [cycle, setCycle] = useState("monthly"); // default to monthly so the first number shown is the per-month price. users can flip to annual for the savings.
 const [loading, setLoading] = useState(true);
 const [buying, setBuying] = useState("");

 useEffect(() => {
 api.get("/billing/plans").then(({ data }) => {
 setPlans(data.plans || []);
 }).finally(() => setLoading(false));
 }, []);

 const planById = (id) => plans.find(p => p.id === id);

 const startCheckout = async (id) => {
 if (id === "free") { navigate("/register"); return; }
 const plan = planById(id);
 if (!plan) return;
 if (!user) { navigate(`/register?next=/pricing`); return; }
 // One-off products (dispute, compass_3yr) can always be re-purchased even if the user's tier matches.
 const oneOffPlans = ["dispute", "compass_3yr"];
 if (user.tier === plan.tier && !oneOffPlans.includes(id)) return;
 const chosenCycle = oneOffPlans.includes(id) ? "oneoff" : cycle;
 setBuying(id);
 try {
 const { data } = await api.post("/billing/checkout", {
 plan_id: id,
 billing_cycle: chosenCycle,
 origin_url: window.location.origin,
 });
 window.location.href = data.url;
 } catch (e) {
 // Surface a friendly toast instead of a native alert. Detect the
 // common case of an expired/invalid Stripe key so we can guide the
 // user back to support instead of asking them to "try again" forever.
 const detail = e.response?.data?.detail || "";
 const looksLikeStripeKeyIssue =
 /stripe|api key|expired|unauthor/i.test(String(detail)) || e.response?.status === 500;
 toast.error(
 looksLikeStripeKeyIssue
 ? "Checkout is temporarily unavailable. Our team has been notified. Please try again in a few minutes or email hello@tenanttrail.net."
 : (detail || "Couldn't start checkout. Please try again."),
 { duration: 6000 }
 );
 setBuying("");
 }
 };

 const priceFor = (id) => {
 if (id === "free") return { amount: 0, label: "forever", noPrefix: true };
 const p = planById(id);
 if (!p) return { amount: null, label: "" };
 if (id === "dispute") return { amount: p.oneoff, label: "one-off · 30 days" };
 if (cycle === "annual") return { amount: p.annual, label: "/year · 1 month free" };
 return { amount: p.monthly, label: "/month" };
 };

 const annualEquivalentMonthly = (id) => {
 const p = planById(id);
 if (cycle !== "annual" || !p?.annual) return null;
 return Math.round((p.annual / 11) * 100) / 100;
 };

 // How much $$ you save by going annual instead of paying monthly × 12.
 const annualSavings = (id) => {
 const p = planById(id);
 if (cycle !== "annual" || !p?.annual || !p?.monthly) return null;
 const saving = Math.round((p.monthly * 12 - p.annual) * 100) / 100;
 return saving > 0 ? saving : null;
 };
 // Best saving across all annual plans, shown next to the Annual toggle.
 const maxAnnualSavings = (() => {
 let max = 0;
 for (const id of TILE_ORDER) {
 const p = planById(id);
 if (!p?.annual || !p?.monthly) continue;
 const s = Math.round((p.monthly * 12 - p.annual) * 100) / 100;
 if (s > max) max = s;
 }
 return max;
 })();

 const isCurrent = (id) => {
 if (!user) return false;
 if (id === "free" && (user.tier === "free" || !user.tier)) return true;
 return user.tier === planById(id)?.tier && id !== "dispute";
 };

 return (
 <div className="min-h-screen paper-grain" data-testid="pricing-page">
 <Navbar />
 <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-14">
 <div className="text-center mb-8">
 <div className="label-mono mb-3">Protection &middot; Evidence &middot; Accountability</div>
 <h1 className="font-serif text-4xl sm:text-5xl text-[#0D2B52] mb-3">Pick the plan for where you are right now</h1>
 <p className="text-[#6B7280] max-w-2xl mx-auto font-serif">
 Every paid plan: full downloads, evidence bundles and a forever-record of your tenancy. Cancel anytime. Switch tiers anytime. Prices in AUD.
 </p>
 <p className="text-[12px] text-[#6B7280] mt-3 max-w-2xl mx-auto italic">
 AI allowances are bounded by plan, daily caps prevent abuse so every user gets fast, reliable answers. Genuinely heavy power user? <a href="mailto:hello@tenanttrail.net" className="underline hover:text-[#1B6C62]">Email us</a> for a fair-use bump.
 </p>
 </div>

 {/* Monthly / Annual toggle */}
 <div className="flex justify-center mb-10">
 <div className="inline-flex border border-[#E5E4DE] bg-white" data-testid="cycle-toggle">
 {[
 ["monthly", "Monthly"],
 ["annual", maxAnnualSavings > 0 ? `Annual · Save up to ${fmtAud(maxAnnualSavings)}` : "Annual · 1 month free"],
 ].map(([k, label]) => (
 <button key={k} onClick={() => setCycle(k)}
 className={`px-4 py-2 text-sm transition-colors ${cycle === k ? "bg-[#0D2B52] text-white" : "text-[#0D2B52] hover:bg-[#FAF9F6]"}`}
 data-testid={`cycle-${k}`}>
 {label}
 </button>
 ))}
 </div>
 </div>

 {loading ? (
 <div className="text-center py-16">
 <Loader2 size={28} strokeWidth={1.5} className="mx-auto text-[#1B6C62] animate-spin mb-2" />
 <p className="text-sm text-[#6B7280]">Loading plans…</p>
 </div>
 ) : (
 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 pt-3">
 {TILE_ORDER.map(id => {
 const tile = TILES[id];
 const { amount, label, noPrefix } = priceFor(id);
 const monthlyEq = annualEquivalentMonthly(id);
 const Icon = tile.icon;
 const featured = id === "copilot";
 return (
 <div
 key={id}
 className={`card flex flex-col relative ${featured ? "ring-2 ring-[#1B6C62] shadow-xl bg-[#F5FBF9] lg:-my-3 lg:py-7 lg:z-10" : ""}`}
 data-testid={`plan-${id}`}
 >
 {/* Floating ribbon above the featured card, draws the eye first */}
 {featured && (
 <div
 className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1B6C62] text-white px-3 py-1 text-[10px] font-mono uppercase tracking-[0.12em] whitespace-nowrap shadow-md flex items-center gap-1.5"
 data-testid="most-popular-ribbon"
 >
 <Star size={10} strokeWidth={2} fill="#FAF6E8" className="text-[#FAF6E8]" /> Most popular
 </div>
 )}
 {/* Badge slot, reserved height so non-featured cards align with featured ones */}
 <div className="min-h-[20px] mb-1">
 {tile.badge && !featured && (
 <div className="label-mono inline-flex items-center gap-1 whitespace-nowrap text-[#B35D4C]">
 <Star size={11} strokeWidth={1.75} fill="#B35D4C" /> {tile.badge}
 </div>
 )}
 </div>
 <div className="flex items-center gap-2 mb-1">
 <div className="w-7 h-7 flex items-center justify-center bg-[#E6F5F2] border border-[#1B6C62]/30 flex-shrink-0">
 <Icon size={13} strokeWidth={1.75} className="text-[#1B6C62]" />
 </div>
 <h3 className="font-serif text-xl text-[#0D2B52]">{tile.name}</h3>
 </div>
 <div className="my-3">
 <div className="font-serif text-3xl text-[#0D2B52]">
 {noPrefix ? "Free" : fmtAud(amount)}
 </div>
 <div className="text-[11px] text-[#6B7280]">{label}</div>
 {monthlyEq && (
 <div className="text-[10.5px] text-[#6B7280] mt-0.5 italic">≈ {fmtAud(monthlyEq)}/mo billed annually</div>
 )}
 {annualSavings(id) && (
 <div className="text-[11px] font-mono text-[#1B6C62] mt-1 inline-block bg-[#E6F5F2] px-1.5 py-0.5" data-testid={`savings-${id}`}>
 Save {fmtAud(annualSavings(id))}/yr
 </div>
 )}
 {id === "copilot" && (
 <div className="mt-2 pt-2 border-t border-[#1B6C62]/15 leading-snug" data-testid="compass-3yr-link">
 <span className="block font-mono uppercase tracking-wider text-[9.5px] text-[#1B6C62] mb-1">Or pay once</span>
 <button type="button" onClick={() => startCheckout("compass_3yr")} disabled={buying === "compass_3yr"} className="text-left block w-full group" data-testid="buy-compass-3yr-inline">
 <span className="block font-serif text-[15px] text-[#0D2B52] group-hover:text-[#1B6C62] transition-colors">3-Year Shield Pass</span>
 <span className="block font-mono text-[11px] text-[#6B7280] mt-0.5">
 <s>$719.64</s> <strong className="font-serif text-[#0D2B52] not-italic">$479</strong> <span className="text-[#B35D4C] font-mono">save $240.64</span>
 </span>
 <span className="block text-[10.5px] text-[#6B7280] mt-0.5 italic">One payment. 3 years. No subscription.</span>
 </button>
 </div>
 )}
 </div>
 <p className="text-[13px] text-[#1A1A1A] font-serif leading-snug mb-3">{tile.blurb}</p>
 <div className="text-[11px] italic text-[#6B7280] mb-3 leading-snug border-l-2 border-[#E5E4DE] pl-2.5">
 &ldquo;{tile.persona}&rdquo;
 </div>
 <ul className="space-y-1.5 mb-5 flex-1">
 {tile.bullets.map((b, i) => (
 <li key={i} className="flex items-start gap-1.5 text-[12.5px] text-[#1A1A1A] leading-snug">
 <Check size={12} strokeWidth={2} className="text-[#1B6C62] mt-0.5 flex-shrink-0" />
 <span>{b}</span>
 </li>
 ))}
 </ul>
 <button
 onClick={() => startCheckout(id)}
 disabled={isCurrent(id) || buying === id}
 className={`mt-auto text-sm py-2.5 px-3 w-full min-h-[48px] leading-tight flex items-center justify-center text-center ${isCurrent(id) ? "bg-[#E5E4DE] text-[#6B7280] cursor-default" : "btn-primary"}`}
 data-testid={`buy-${id}`}
 >
 {isCurrent(id)
 ? "Your current plan"
 : buying === id
 ? "Opening Stripe…"
 : tile.cta
 ? tile.cta
 : (id === "dispute" ? "Activate Dispute Mode" : `Choose ${tile.name}`)}
 </button>
 </div>
 );
 })}
 </div>
 )}

 {/* Dispute & Tribunal Mode — pulled out of the subscription grid so it reads as a focused, one-off product. Designed to mirror the visual weight of a tier card (icon + title + price + bullets + CTA) but in a wider, centered, ochre/amber-accented layout that says "this is the urgent one, not a subscription". */}
 <div className="mb-16" data-testid="dispute-band">
 <div className="max-w-3xl mx-auto relative pt-3">
 {/* Pay once ribbon */}
 <div
 className="absolute -top-0 left-1/2 -translate-x-1/2 bg-[#B35D4C] text-white px-3 py-1 text-[10px] font-mono uppercase tracking-[0.12em] whitespace-nowrap shadow-md flex items-center gap-1.5 z-10"
 data-testid="dispute-pay-once-ribbon"
 >
 <Zap size={10} strokeWidth={2} fill="#FAF6E8" className="text-[#FAF6E8]" /> Pay once, no subscription
 </div>
 <div className="card relative bg-[#FFF7F2] border-l-4 border-l-[#B35D4C] pt-7 pb-6">
 {/* Top row: identity + price */}
 <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-3">
 <div className="flex items-start gap-3">
 <div className="w-9 h-9 flex items-center justify-center bg-[#B35D4C]/10 border border-[#B35D4C]/30 flex-shrink-0">
 <Zap size={16} strokeWidth={1.75} className="text-[#B35D4C]" />
 </div>
 <div>
 <div className="label-mono text-[#B35D4C] mb-0.5">Already in a dispute?</div>
 <h3 className="font-serif text-2xl text-[#0D2B52] leading-tight">Dispute & Tribunal Mode</h3>
 <p className="text-[12.5px] text-[#1A1A1A] font-serif leading-snug mt-1 max-w-md">
 {TILES.dispute.blurb}
 </p>
 </div>
 </div>
 <div className="text-left sm:text-right sm:pl-4 sm:border-l sm:border-[#B35D4C]/20 flex-shrink-0">
 <div className="font-serif text-4xl text-[#0D2B52] leading-none">$69</div>
 <div className="text-[11px] text-[#6B7280] mt-1">one-off · 30 days</div>
 <div className="text-[10.5px] text-[#B35D4C] font-mono mt-0.5">No auto-renew</div>
 </div>
 </div>

 {/* Persona quote */}
 <div className="text-[12px] italic text-[#6B7280] mb-3 leading-snug border-l-2 border-[#B35D4C]/30 pl-2.5">
 &ldquo;{TILES.dispute.persona}&rdquo;
 </div>

 {/* Bullets in 2 columns, full card width */}
 <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-4">
 {TILES.dispute.bullets.map((b, i) => (
 <li key={i} className="flex items-start gap-1.5 text-[12.5px] text-[#1A1A1A] leading-snug">
 <Check size={12} strokeWidth={2} className="text-[#B35D4C] mt-0.5 flex-shrink-0" />
 <span>{b}</span>
 </li>
 ))}
 </ul>

 {/* CTA row */}
 <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-3 border-t border-[#B35D4C]/15">
 <p className="text-[11.5px] text-[#6B7280] italic leading-snug flex-1">
 Tribunal on Thursday. Bundle due Friday. We turn the dial up so you can move fast.
 </p>
 <button
 onClick={() => startCheckout("dispute")}
 disabled={buying === "dispute"}
 className="text-sm py-2.5 px-6 text-white whitespace-nowrap transition-colors hover:opacity-90 sm:w-auto w-full"
 style={{ background: "#B35D4C" }}
 data-testid="buy-dispute"
 >
 {buying === "dispute" ? "Opening Stripe…" : "Activate Dispute Mode →"}
 </button>
 </div>
 </div>
 </div>
 </div>

 {/* Phone-first upload callout. earned from the founder's own QCAT case */}
 <div className="mb-16" data-testid="phone-upload-callout">
 <div className="relative border-l-4 border-l-[#1B6C62] bg-[#E6F5F2]/30 px-6 py-8 sm:px-10 sm:py-10 overflow-hidden">
 <div className="grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-center">
 {/* Copy column */}
 <div className="max-w-2xl">
 <div className="label-mono mb-2 text-[#1B6C62] flex items-center gap-1.5">
 <Smartphone size={11} strokeWidth={1.75} />
 <span>Built by someone who&rsquo;s been there</span>
 </div>
 <h2 className="font-serif text-2xl sm:text-3xl text-[#0D2B52] leading-tight mb-3">
 Upload straight from your phone, we look after the rest.
 </h2>
 <p className="text-[14px] sm:text-[15px] text-[#1A1A1A] leading-relaxed mb-6 max-w-2xl">
 Anyone who&rsquo;s rented in Australia knows the dance: shrink that photo so it&rsquo;ll fit in an email. Convert your iPhone or Android photos into something the agent can actually open. Hope the file isn&rsquo;t too big. We&rsquo;ve been there. Now we do all that for you, in the background, the moment you tap upload.
 </p>
 <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
 <div className="flex items-start gap-2.5" data-testid="phone-upload-benefit-1">
 <ImageIcon size={16} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5 shrink-0" />
 <div>
 <div className="font-serif text-[14px] text-[#0D2B52] mb-0.5">Drop in 100 photos at once</div>
 <p className="text-[12px] text-[#5a6b7a] leading-snug">
 Snap, drop, done. No shrinking, no resizing, no &ldquo;file too large&rdquo; emails. We handle it.
 </p>
 </div>
 </div>
 <div className="flex items-start gap-2.5" data-testid="phone-upload-benefit-2">
 <Fingerprint size={16} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5 shrink-0" />
 <div>
 <div className="font-serif text-[14px] text-[#0D2B52] mb-0.5">The truth, locked in</div>
 <p className="text-[12px] text-[#5a6b7a] leading-snug">
 Each photo&rsquo;s date stays safe with it. If anyone tries to twist the story later, the proof&rsquo;s already on file.
 </p>
 </div>
 </div>
 <div className="flex items-start gap-2.5" data-testid="phone-upload-benefit-3">
 <ShieldCheck size={16} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5 shrink-0" />
 <div>
 <div className="font-serif text-[14px] text-[#0D2B52] mb-0.5">iPhone or Android, doesn&rsquo;t matter</div>
 <p className="text-[12px] text-[#5a6b7a] leading-snug">
 Every photo format works. We quietly convert anything weird so the agent (and the tribunal, if it ever comes to that) can always open it.
 </p>
 </div>
 </div>
 </div>
 </div>
 {/* Phone mockup column — hidden on mobile to keep the section compact */}
 <div className="hidden lg:flex justify-center items-center pl-4">
 <PhoneMockup />
 </div>
 </div>
 </div>
 </div>

 {/* Feature comparison matrix */}
 <div className="mb-16" data-testid="feature-matrix">
 <div className="text-center mb-6">
 <div className="label-mono mb-2 text-[#1B6C62]">Compare every feature</div>
 <h2 className="font-serif text-2xl sm:text-3xl text-[#0D2B52]">Side by side</h2>
 <p className="text-[13px] text-[#6B7280] mt-2 max-w-xl mx-auto italic font-serif">
 Most renters land on <strong className="not-italic text-[#0D2B52]">Shield</strong>. It unlocks Deep AI, letter drafting and voice-to-statement, which is where the real time-savings live.
 </p>
 </div>
 <div className="overflow-x-auto -mx-4 sm:mx-0 rounded-lg bg-white border border-[#E5E4DE] shadow-[0_18px_40px_-24px_rgba(13,43,82,0.22),0_2px_6px_-2px_rgba(13,43,82,0.06)]" data-testid="feature-matrix-table-wrap">
 <table className="w-full min-w-[720px] text-sm border-separate border-spacing-0" data-testid="feature-matrix-table">
 <thead>
 <tr>
 <th className="text-left align-middle py-3 px-4 font-serif font-normal text-[#0D2B52] w-[34%] bg-[#FAF9F6] border-b border-[#E5E4DE] rounded-tl-lg">
 <span className="label-mono text-[#6B7280]">What you get</span>
 </th>
 {TILE_ORDER.map((id, idx) => {
 const tile = TILES[id];
 const featured = id === "copilot";
 const isLast = idx === TILE_ORDER.length - 1;
 const { amount, label, noPrefix } = priceFor(id);
 return (
 <th
 key={id}
 className={`text-center align-middle py-3 px-3 font-normal border-b ${
 featured
 ? "bg-[#0D2B52] text-white border-b-[#0D2B52]"
 : "text-[#0D2B52] bg-[#FAF9F6] border-b-[#E5E4DE]"
 } ${isLast && !featured ? "rounded-tr-lg" : ""}`}
 data-testid={`matrix-header-${id}`}
 >
 {featured && (
 <div className="font-mono text-[9px] tracking-[0.14em] uppercase text-[#F5E1B7] mb-0.5">Most popular</div>
 )}
 <div className={`font-serif text-[17px] leading-tight ${featured ? "text-white" : "text-[#0D2B52]"}`}>{tile.name}</div>
 <div className={`mt-0.5 font-mono text-[12px] ${featured ? "text-white" : "text-[#1A1A1A]"}`}>
 {amount === 0 || noPrefix ? "Free" : (amount != null ? fmtAud(amount) : "n/a")}
 <span className={`ml-0.5 ${featured ? "text-white/60" : "text-[#6B7280]"}`}>{amount === 0 || noPrefix ? "" : label}</span>
 </div>
 </th>
 );
 })}
 </tr>
 </thead>
 <tbody>
 {FEATURE_MATRIX.map((row, i) => {
 // Section header row. Sandy band, no zebra, thin bottom divider.
 if (row.section) {
 return (
 <tr key={`sec-${i}`} data-testid={`matrix-section-${row.section.toLowerCase().replace(/\s+/g, "-")}`}>
 <td colSpan={TILE_ORDER.length} className="pt-3 pb-1.5 px-4 bg-[#FAF9F6] border-t border-b border-[#EDEBE4]">
 <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#1B6C62]">
 {row.section}
 </span>
 </td>
 {/* Featured column keeps its tint through the section band so the column edge stays clean */}
 <td className="pt-3 pb-1.5 px-4 bg-[#EEF6F3] border-t border-b border-[#E1EEEA]" />
 </tr>
 );
 }
 // Data row — solid white on non-featured cells, solid tint on featured. No zebra, thin dividers only.
 return (
 <tr key={i} className="bg-white">
 <td className="py-2 px-4 text-[#1A1A1A] font-serif text-[13px] leading-snug border-b border-[#F0EEE9]">{row.row}</td>
 {TILE_ORDER.map((id) => {
 const featured = id === "copilot";
 return (
 <td
 key={id}
 className={`text-center py-2 px-3 border-b ${
 featured
 ? "bg-[#EEF6F3] border-b-[#DFEBE7]"
 : "border-b-[#F0EEE9]"
 }`}
 >
 {row[id] === true ? (
 <Check size={14} strokeWidth={2.25} className={`mx-auto ${featured ? "text-[#0D2B52]" : "text-[#1B6C62]"}`} />
 ) : row[id] === false ? (
 <Minus size={13} strokeWidth={2} className="mx-auto text-[#D4CFC5]" />
 ) : (
 <span className={`text-[12px] font-mono ${featured ? "text-[#0D2B52] font-semibold" : "text-[#1A1A1A]"}`}>
 {row[id]}
 </span>
 )}
 </td>
 );
 })}
 </tr>
 );
 })}

 {/* CTA row */}
 <tr data-testid="matrix-cta-row" className="bg-white">
 <td className="pt-3 pb-3 px-4 align-middle border-t border-[#E5E4DE] rounded-bl-lg">
 <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#6B7280]">Ready?</span>
 </td>
 {TILE_ORDER.map((id, idx) => {
 const featured = id === "copilot";
 const isFree = id === "free";
 const currentlyOnThis = isCurrent(id);
 const label = currentlyOnThis
 ? "Current plan"
 : isFree
 ? "Start free"
 : featured
 ? "Choose Shield"
 : `Choose ${TILES[id].name}`;
 return (
 <td
 key={id}
 className={`text-center pt-3 pb-3 px-3 align-middle border-t border-[#E5E4DE] ${
 featured ? "bg-[#EEF6F3]" : ""
 } ${idx === TILE_ORDER.length - 1 ? "rounded-br-lg" : ""}`}
 >
 <button
 type="button"
 disabled={currentlyOnThis}
 onClick={() => {
 if (currentlyOnThis) return;
 if (isFree) {
 navigate(user ? "/dashboard" : "/register");
 } else {
 startCheckout(id);
 }
 }}
 className={`w-full text-[12px] px-3 py-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
 featured
 ? "bg-[#0D2B52] text-white hover:brightness-110 font-medium"
 : "border border-[#0D2B52] text-[#0D2B52] hover:bg-[#0D2B52] hover:text-white"
 }`}
 data-testid={`matrix-cta-${id}`}
 >
 {label}
 </button>
 </td>
 );
 })}
 </tr>
 </tbody>
 </table>
 </div>
 </div>

 {/* FAQ */}
 <div className="max-w-3xl mx-auto mb-12">
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-6 text-center">Questions about paying</h2>
 <div className="space-y-5">
 <Faq q="What&rsquo;s the difference between Quick and Deep chats?" a="Quick chats are short, friendly answers, perfect for questions like &lsquo;can my landlord enter without notice?&rsquo;. Deep chats take a few seconds longer and let Compass read your state&rsquo;s rental laws AND your own documents so the answer is specific to YOU. Deep chats can also draft letters and prepare clear statements for you. Free and Vault tiers get Quick chats; Shield, Dispute Mode and Pro get Deep chats." />
 <Faq q="What&rsquo;s &lsquo;Shield&rsquo;? Is that the same as Compass?" a="Compass is the friendly name for our renting helper. Ask it anything in plain English and it&rsquo;ll quote the exact rule in your state. Shield is a plan name. It&rsquo;s the most popular plan because it unlocks everything Compass can do for you: 100 chats a month, letter drafting, voice statements, monthly photo check-ins. Compass the AI is available on every plan (with different chat limits). Shield the plan is what most renters land on for full access." />
 <Faq q="Can I cancel anytime?" a="Yes. Monthly plans cancel from your Account page, they keep working until the end of the period you&rsquo;ve paid for. Dispute Mode is a one-off; nothing to cancel." />
 <Faq q="What happens to my photos and documents if I downgrade?" a="They stay in your account, always. On the free plan you can still view everything in-app, you just can&rsquo;t download or send out an evidence bundle until you upgrade again. Storage caps apply on free." />
 <Faq q="Why is Shield &lsquo;Most popular&rsquo;?" a="It&rsquo;s the plan most renters need: 100 friendly Compass chats a month, polite letter drafting, voice statements and the monthly photo check-ins. Everything you need to rent with peace of mind, without paying for headroom you&rsquo;ll never use." />
 <Faq q="What if something already went wrong?" a="Dispute Mode is built for exactly that. $69 one-off, 500 AI queries over 30 days. Buy it, sort out the mess, move on. No subscription." />
 <Faq q="Is this legal advice?" a="No. TenantTrail is a friendly helper that organises your tenancy and explains the rules in plain English. For actual legal advice, your state&rsquo;s Tenants&rsquo; Union is free, Compass will point you to the right one." />
 <Faq q="Australian dollars?" a="Yes. All prices and Stripe charges are in AUD. Includes GST where applicable." />
 </div>
 </div>

 <p className="text-xs text-[#6B7280] text-center mt-10 max-w-xl mx-auto italic font-serif">
 Payments are processed securely by Stripe. You can cancel any monthly plan at any time from your Account page, the plan keeps working until the period ends.
 </p>
 <p className="text-[11px] text-[#9CA3AF] text-center mt-3 font-mono">
 <Link to="/refunds" className="hover:text-[#0D2B52] underline-offset-2 hover:underline" data-testid="pricing-refunds-link">
 Refunds &amp; cancellations policy
 </Link>
 <span className="mx-2 text-[#D0CBB8]">·</span>
 <Link to="/trust" className="hover:text-[#0D2B52] underline-offset-2 hover:underline" data-testid="pricing-trust-link">
 How we keep your documents safe
 </Link>
 </p>
 </div>
 <SiteFooter />
 </div>
 );
}

function Faq({ q, a }) {
 return (
 <details className="border border-[#E5E4DE] bg-white p-4 group" data-testid={`faq-${q.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}>
 <summary className="cursor-pointer font-serif text-[15px] text-[#0D2B52] list-none flex justify-between items-center">
 {q}
 <span className="text-[#1B6C62] text-lg group-open:rotate-45 transition-transform leading-none">+</span>
 </summary>
 <p className="mt-3 text-sm text-[#1A1A1A] font-serif leading-relaxed">{a}</p>
 </details>
 );
}
